import { useEffect, useState, FormEvent } from 'react';
import { client } from '../../sdkAdapter';
import {
    BrandRecord,
    CatalogImageRecord,
    CatalogPagination,
    extractApiErrorMessage,
    extractBrandCollection,
    sanitizeSlug,
    toBoolean,
} from './catalogApi';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import Badge from "../ui/badge/Badge";
import Pagination from "../ui/pagination";
import MediaUploader from "../common/MediaUploader";
import CatalogModalFrame from './CatalogModalFrame';
import {
    confirmAction,
    showErrorAlert,
    showSuccessToast,
    showValidationAlert,
} from './catalogAlerts';

interface BrandFormState {
    name: string;
    slug: string;
    description: string;
    sort: string;
    active: boolean;
    top: boolean;
    show_home: boolean;
    image_id?: number;
}

interface ValidatedBrandForm {
    name: string;
    slug?: string;
    description?: string;
    sort?: number;
    active: boolean;
    top: boolean;
    show_home: boolean;
    image_id?: number;
}

const emptyForm = (): BrandFormState => ({
    name: '',
    slug: '',
    description: '',
    sort: '0',
    active: true,
    top: false,
    show_home: false,
    image_id: undefined,
});

const getFullUrl = (url?: string | null): string => {
    if (!url) {
        return '';
    }

    if (url.startsWith('http://') || url.startsWith('https://')) {
        return encodeURI(url);
    }

    const baseUrl = (import.meta.env.VITE_API_BASE || 'http://127.0.0.1:8000').replace(/\/$/, '');
    const cleanUrl = url.replace(/^\//, '');

    return encodeURI(`${baseUrl}/${cleanUrl}`);
};

const getImageSource = (image?: CatalogImageRecord | null): string => {
    const path = image?.path || image?.thumb_r || image?.thumb_m || image?.thumb_s || '';
    return getFullUrl(path);
};

const validateBrandForm = (form: BrandFormState): { valid: true; data: ValidatedBrandForm; } | { valid: false; message: string; } => {
    const name = form.name.trim();
    const slug = sanitizeSlug(form.slug);
    const description = form.description.trim();
    const sortValue = form.sort.trim();

    if (!name) {
        return { valid: false, message: 'Brand name is required.' };
    }

    if (name.length > 50) {
        return { valid: false, message: 'Brand name must be 50 characters or fewer.' };
    }

    if (form.slug.trim() && !slug) {
        return { valid: false, message: 'Slug can only contain letters, numbers, and hyphens.' };
    }

    let sort: number | undefined;
    if (sortValue) {
        sort = Number(sortValue);
        if (!Number.isInteger(sort) || sort < 0) {
            return { valid: false, message: 'Sort order must be a whole number greater than or equal to 0.' };
        }
    }

    return {
        valid: true,
        data: {
            name,
            ...(slug ? { slug } : {}),
            ...(description ? { description } : {}),
            ...(typeof sort === 'number' ? { sort } : {}),
            ...(typeof form.image_id === 'number' ? { image_id: form.image_id } : {}),
            active: form.active,
            top: form.top,
            show_home: form.show_home,
        }
    };
};

export default function CatalogBrands() {
    const [brands, setBrands] = useState<BrandRecord[]>([]);
    const [imageUrlsById, setImageUrlsById] = useState<Record<number, string>>({});
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [pagination, setPagination] = useState<CatalogPagination | null>(null);
    const [form, setForm] = useState<BrandFormState>(emptyForm);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(15);

    useEffect(() => {
        fetchBrands(currentPage, rowsPerPage);
    }, [currentPage, rowsPerPage]);

    const fetchBrands = async (page = currentPage, perPage = rowsPerPage) => {
        setLoading(true);
        try {
            const res = await client.getApiCatalogBrands({}, {
                params: {
                    page,
                    per_page: perPage,
                }
            }) as any;
            const { items, pagination: nextPagination } = extractBrandCollection(res);

            setBrands(items);
            setPagination(nextPagination);

            if (nextPagination) {
                if (nextPagination.currentPage !== currentPage) {
                    setCurrentPage(nextPagination.currentPage);
                }
                if (nextPagination.perPage > 0 && nextPagination.perPage !== rowsPerPage) {
                    setRowsPerPage(nextPagination.perPage);
                }
            }
        } catch (err) {
            console.error('Failed to fetch brands:', err);
            setBrands([]);
            setPagination(null);
            await showErrorAlert(extractApiErrorMessage(err, 'Failed to load brands'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const missingImageIds = brands
            .map((brand) => brand.image_id)
            .filter((id): id is number => typeof id === 'number' && id > 0)
            .filter((id) => !imageUrlsById[id]);

        if (missingImageIds.length === 0) {
            return;
        }

        const uniqueIds = Array.from(new Set(missingImageIds));

        const fetchImageUrls = async () => {
            try {
                const responses = await Promise.all(
                    uniqueIds.map((id) => client.getApiMediaFilesId({ id: String(id) }) as any)
                );

                const nextEntries: Array<[number, string]> = responses
                    .map((res, index) => {
                        const responseData = res?.data?.data;
                        const file = Array.isArray(responseData) ? responseData[0] : responseData;
                        const src = getImageSource(file);
                        return [uniqueIds[index], src] as [number, string];
                    })
                    .filter(([, src]) => Boolean(src));

                if (nextEntries.length > 0) {
                    setImageUrlsById((prev) => ({
                        ...prev,
                        ...Object.fromEntries(nextEntries),
                    }));
                }
            } catch (err) {
                console.error('Failed to resolve brand image URLs:', err);
            }
        };

        fetchImageUrls();
    }, [brands, imageUrlsById]);

    const handleEdit = (brand: BrandRecord) => {
        setEditingId(brand.id);
        setForm({
            name: brand.name || '',
            slug: brand.slug || '',
            description: brand.description || '',
            sort: typeof brand.sort === 'number' ? String(brand.sort) : '0',
            active: toBoolean(brand.active ?? true),
            top: toBoolean(brand.top ?? false),
            show_home: toBoolean(brand.show_home ?? false),
            image_id: brand.image_id || brand.images?.id,
        });
        setIsModalOpen(true);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setForm(emptyForm());
        setIsModalOpen(false);
    };

    const refreshCurrentPage = async () => {
        await fetchBrands(currentPage, rowsPerPage);
    };

    const createBrand = async () => {
        const validated = validateBrandForm(form);
        if (!validated.valid) {
            await showValidationAlert(validated.message);
            return;
        }

        setSubmitting(true);
        try {
            await client.postApiCatalogBrandCreate({
                postApiCatalogBrandCreateRequest: validated.data
            });
            handleCancelEdit();
            setCurrentPage(1);
            await fetchBrands(1, rowsPerPage);
            showSuccessToast('Brand created successfully');
        } catch (err: any) {
            await showErrorAlert(extractApiErrorMessage(err, 'Failed to create brand'));
        } finally {
            setSubmitting(false);
        }
    };

    const updateBrand = async () => {
        if (editingId === null) {
            return;
        }

        const validated = validateBrandForm(form);
        if (!validated.valid) {
            await showValidationAlert(validated.message);
            return;
        }

        setSubmitting(true);
        try {
            await client.postApiCatalogBrandUpdate({
                postApiCatalogBrandUpdateRequest: {
                    id: editingId,
                    ...validated.data,
                }
            });
            handleCancelEdit();
            await refreshCurrentPage();
            showSuccessToast('Brand updated successfully');
        } catch (err: any) {
            await showErrorAlert(extractApiErrorMessage(err, 'Failed to update brand'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        if (editingId !== null) {
            await updateBrand();
            return;
        }

        await createBrand();
    };

    const handleDelete = async (brandId: number) => {
        const isConfirmed = await confirmAction({
            text: 'This brand will be permanently deleted.',
            confirmButtonText: 'Delete Brand'
        });

        if (!isConfirmed) {
            return;
        }

        try {
            await client.postApiCatalogBrandDelete({
                postApiCatalogBrandDeleteRequest: { id: brandId }
            });

            if (editingId === brandId) {
                handleCancelEdit();
            }

            await refreshCurrentPage();
            showSuccessToast('Brand deleted successfully');
        } catch (err: any) {
            console.error(err);
            await showErrorAlert(extractApiErrorMessage(err, 'Failed to delete brand'));
        }
    };

    const totalItems = pagination?.total ?? brands.length;
    const totalPages = Math.max(1, pagination?.lastPage ?? 1);

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Brands</h2>
                <button
                    onClick={() => {
                        handleCancelEdit();
                        setIsModalOpen(true);
                    }}
                    className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:bg-brand-600 active:scale-95"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create Brand
                </button>
            </div>

            <CatalogModalFrame
                isOpen={isModalOpen}
                onClose={handleCancelEdit}
                title={editingId !== null ? 'Edit Brand' : 'Create Brand'}
                className="max-w-2xl"
                formId="catalog-brand-form"
                submitLabel={submitting ? 'Processing...' : editingId !== null ? 'Update Brand' : 'Create Brand'}
                submitDisabled={submitting}
            >
                <form id="catalog-brand-form" onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Brand Logo</label>
                        <MediaUploader
                            onUpload={(ids) => setForm((prev) => ({ ...prev, image_id: ids[0] }))}
                            initialImages={form.image_id ? [form.image_id] : []}
                            multiple={false}
                            folder="brands"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                maxLength={50}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm"
                                placeholder="Enter brand name..."
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Slug</label>
                            <input
                                type="text"
                                value={form.slug}
                                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                                maxLength={100}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm"
                                placeholder="Auto-generated if left blank"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Description</label>
                        <textarea
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            rows={3}
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm"
                            placeholder="Optional brand description"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Sort Order</label>
                            <input
                                type="number"
                                min={0}
                                value={form.sort}
                                onChange={(e) => setForm({ ...form, sort: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Status</label>
                            <select
                                value={form.active ? 'true' : 'false'}
                                onChange={(e) => setForm({ ...form, active: e.target.value === 'true' })}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm appearance-none cursor-pointer"
                            >
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <label className="flex items-center gap-3 cursor-pointer group rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3">
                            <input
                                type="checkbox"
                                checked={form.top}
                                onChange={(e) => setForm({ ...form, top: e.target.checked })}
                                className="w-4 h-4 text-brand-500 border-gray-300 rounded focus:ring-brand-500"
                            />
                            <span className="text-sm font-bold text-gray-700 dark:text-gray-300 group-hover:text-brand-500 transition-colors">Featured / Top Brand</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer group rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3">
                            <input
                                type="checkbox"
                                checked={form.show_home}
                                onChange={(e) => setForm({ ...form, show_home: e.target.checked })}
                                className="w-4 h-4 text-brand-500 border-gray-300 rounded focus:ring-brand-500"
                            />
                            <span className="text-sm font-bold text-gray-700 dark:text-gray-300 group-hover:text-brand-500 transition-colors">Show on Home</span>
                        </label>
                    </div>
                </form>
            </CatalogModalFrame>

            <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden shadow-sm">
                <div className="p-5 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50 flex items-center justify-between gap-4">
                    <div>
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white">Existing Brands</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {loading ? 'Loading brand records...' : `${totalItems} brand${totalItems === 1 ? '' : 's'} available.`}
                        </p>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableCell isHeader className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider w-20">Image</TableCell>
                                <TableCell isHeader className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Brand</TableCell>
                                <TableCell isHeader className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Slug</TableCell>
                                <TableCell isHeader className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-center">Sort</TableCell>
                                <TableCell isHeader className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-center">Flags</TableCell>
                                <TableCell isHeader className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-center">Status</TableCell>
                                <TableCell isHeader className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-end">Actions</TableCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400 font-medium">
                                        Loading brands...
                                    </TableCell>
                                </TableRow>
                            ) : brands.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400 font-medium">
                                        No brands found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                brands.map((brand) => {
                                    const brandImage = getImageSource(brand.images) || (brand.image_id ? imageUrlsById[brand.image_id] || '' : '');

                                    return (
                                    <TableRow key={brand.id} className={`group transition-all hover:bg-gray-50 dark:hover:bg-gray-800/50 ${editingId === brand.id ? 'bg-brand-50/50 dark:bg-brand-900/10' : ''}`}>
                                        <TableCell className="px-6 py-4">
                                            {brandImage ? (
                                                <img
                                                    src={brandImage}
                                                    alt={brand.name}
                                                    className="w-10 h-10 rounded-lg object-cover border border-gray-200 dark:border-gray-700"
                                                />
                                            ) : (
                                                <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xs text-gray-400 font-bold border border-gray-200 dark:border-gray-700">
                                                    {brand.name.substring(0, 2).toUpperCase()}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell className="px-6 py-4">
                                            <p className="font-bold text-gray-700 dark:text-gray-200">{brand.name}</p>
                                            {brand.description ? (
                                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{brand.description}</p>
                                            ) : null}
                                        </TableCell>
                                        <TableCell className="px-6 py-4 font-mono text-xs text-gray-500">{brand.slug || '-'}</TableCell>
                                        <TableCell className="px-6 py-4 text-center font-bold text-gray-600 dark:text-gray-400">
                                            {typeof brand.sort === 'number' ? brand.sort : 0}
                                        </TableCell>
                                        <TableCell className="px-6 py-4 text-center">
                                            <div className="flex items-center justify-center gap-2">
                                                {toBoolean(brand.top) ? <Badge color="info" size="sm">Top</Badge> : null}
                                                {toBoolean(brand.show_home) ? <Badge color="light" size="sm">Home</Badge> : null}
                                                {!toBoolean(brand.top) && !toBoolean(brand.show_home) ? (
                                                    <span className="text-xs text-gray-400">-</span>
                                                ) : null}
                                            </div>
                                        </TableCell>
                                        <TableCell className="px-6 py-4 text-center">
                                            <Badge color={toBoolean(brand.active) ? 'success' : 'error'} size="sm">
                                                {toBoolean(brand.active) ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="px-6 py-4 text-end">
                                            <div className="flex justify-end gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => handleEdit(brand)}
                                                    className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-brand-500 hover:border-brand-200 shadow-sm transition-all active:scale-95"
                                                    title="Edit Brand"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(brand.id)}
                                                    className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-red-500 hover:border-red-200 shadow-sm transition-all active:scale-95"
                                                    title="Delete Brand"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>
                {!loading && totalItems > 0 ? (
                    <Pagination
                        currentPage={pagination?.currentPage ?? currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        rowsPerPage={pagination?.perPage ?? rowsPerPage}
                        onRowsPerPageChange={(rows) => {
                            setRowsPerPage(rows);
                            setCurrentPage(1);
                        }}
                        totalItems={totalItems}
                    />
                ) : null}
            </div>
        </div>
    );
}
